import { formatScheduleTime, formatUSD, formatNumbersInText, getCoinContext, localizedAnalysisField, localizedMarketField, normalizeMarketProse, scheduleDisplayStatus } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Target, ShieldAlert, TrendingUp, Zap } from "lucide-react";

const SIGNAL_STYLES = {
  ENTER: { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-400", fill: "bg-emerald-400", glow: "shadow-[0_0_30px_rgba(34,197,94,0.25)]" },
  WAIT:  { bg: "bg-amber-500/10",   border: "border-amber-500/40",   text: "text-amber-400",   fill: "bg-amber-400",   glow: "shadow-[0_0_30px_rgba(245,158,11,0.2)]" },
  AVOID: { bg: "bg-red-500/10",     border: "border-red-500/40",     text: "text-red-400",     fill: "bg-red-400",     glow: "shadow-[0_0_30px_rgba(239,68,68,0.25)]" },
};

export default function AIAnalysis({ symbol, analysis, marketContext, schedule, now = Date.now(), statusAvailable, desktopHeight }) {
  const { t, lang } = useI18n();
  const loading = !analysis;
  const technical = schedule?.technical;
  const statusText = scheduleDisplayStatus(schedule, "technical", now) === "updating" ? t("updating") : scheduleDisplayStatus(schedule, "technical", now);
  const lastTime = formatScheduleTime(technical?.last_completed_at, schedule?.timezone);
  const contextSchedule = schedule?.market_context;
  const contextDisplayStatus = scheduleDisplayStatus(schedule, "market_context", now);
  const contextStatus = contextDisplayStatus === "updating" ? t("updating") : contextDisplayStatus === "unavailable" ? t("status_unavailable") : contextDisplayStatus;
  const coinContext = getCoinContext(marketContext, symbol);

  const sig = SIGNAL_STYLES[analysis?.signal] || SIGNAL_STYLES.WAIT;
  const confidence = Math.max(0, Math.min(100, Number(analysis?.confidence) || 0));

  return (
    <div
      className="tracing-border border border-border/60 rounded-sm bg-[#09090B] p-4 relative h-auto lg:min-h-0 overflow-hidden flex flex-col"
      style={desktopHeight ? { height: `${desktopHeight}px` } : undefined}
      data-testid="ai-analysis-panel"
    >
      <div className="min-h-0 lg:flex-1 lg:overflow-y-auto overflow-visible pr-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-700/60 [&::-webkit-scrollbar-track]:bg-transparent">
      {loading && !analysis && (
        <div className="space-y-2 relative z-10">
          <div className="shimmer h-20 rounded-sm" />
          <div className="shimmer h-10 rounded-sm" />
          <div className="shimmer h-24 rounded-sm" />
        </div>
      )}

      {analysis && (
        <div className="space-y-4 relative z-10" data-testid="ai-analysis-result">
          {/* Signal card */}
          <div className={`rounded-sm border ${sig.border} ${sig.bg} ${sig.glow} px-3 py-3`}>
            <div className="mb-1.5">
              <span className="text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-zinc-400">Signal · {symbol}</span>
            </div>
            <div className={`font-display font-black text-lg sm:text-xl leading-tight ${sig.text}`} data-testid="ai-signal-badge">
              {t(analysis.signal)}
            </div>
            <div className="mt-2 flex items-center gap-2 text-[10px] sm:text-[11px] font-mono text-zinc-400">
              <Zap className="w-3 h-3" />
              <div
                className="flex-1 h-2 bg-zinc-800 border border-white/10 rounded-sm overflow-hidden"
                role="progressbar"
                aria-label={`${t("confidence")}: ${confidence}%`}
                aria-valuenow={confidence}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div className={`h-full ${sig.fill} transition-[width] duration-300`} style={{ width: `${confidence}%` }} />
              </div>
              <span className="text-white">{confidence}%</span>
            </div>
          </div>

          {/* Entry price */}
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3">
            <div className="flex items-center gap-1 text-[10px] sm:text-[11px] font-mono uppercase text-emerald-400 mb-1">
              <Target className="w-2.5 h-2.5" /> {t("entry_price")}
            </div>
            <div className="font-mono text-2xl sm:text-3xl font-bold text-white leading-tight" data-testid="ai-entry-price">
              {formatUSD(analysis.entry_price)}
            </div>
            {analysis.tech && (
              <div className="text-[10px] sm:text-[11px] text-zinc-500 font-mono mt-1 leading-[1.45]">
                S {formatUSD(analysis.tech.support)} · R {formatUSD(analysis.tech.resistance)}
              </div>
            )}
          </div>

          {/* Target & Stop */}
          <div className="grid grid-cols-2 gap-2">
              <div className="border border-emerald-500/30 rounded-sm p-2.5">
              <div className="flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-400">
                <TrendingUp className="w-2.5 h-2.5" /> {t("target")}
              </div>
              <div className="font-mono text-[13px] sm:text-sm text-emerald-300 mt-1">{formatUSD(analysis.target_price)}</div>
            </div>
            <div className="border border-red-500/30 rounded-sm p-2.5">
              <div className="flex items-center gap-1 text-[10px] font-mono uppercase text-red-400">
                <ShieldAlert className="w-2.5 h-2.5" /> {t("stop")}
              </div>
              <div className="font-mono text-[13px] sm:text-sm text-red-300 mt-1">{formatUSD(analysis.stop_loss)}</div>
            </div>
          </div>

          {/* When to enter */}
          <div className="border border-border/60 rounded-sm px-3 py-2.5">
            <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-1">{t("when_to_enter")}</div>
            <div className="text-[10px] sm:text-xs text-white leading-[1.45] break-words">{formatNumbersInText(localizedAnalysisField(analysis.localized?.good_entry_time || analysis.good_entry_time, lang, t("unavailable")))}</div>
          </div>

          <div id="technical-analysis" className="border-t border-amber-500/30 pt-3 mt-2" data-testid="technical-analysis-block">
            <AnalysisMetadata
              title={t("technical_analysis")}
              lastTime={lastTime}
              nextTime={formatScheduleTime(technical?.next_update_at, schedule?.timezone)}
              status={statusAvailable === false || schedule?.technical_error ? t("status_unavailable") : statusText}
            />
          </div>

          <div className="space-y-2">
            <div data-testid="technical-context-section">
              {/* Keep the canonical English source explicit for layout/source compatibility: analysis.technical) */}
              <ContextSection title={t("technical_context")} value={formatNumbersInText(localizedAnalysisField(analysis.localized?.technical || analysis.technical, lang, t("unavailable")))} />
            </div>
            <div data-testid="technical-bottom-line-section">
              {/* Canonical field retained for compatibility: analysis.technical_bottom_line) */}
              <ContextSection title={t("technical_bottom_line")} value={formatNumbersInText(localizedAnalysisField(analysis.localized?.technical_bottom_line || analysis.technical_bottom_line, lang, t("unavailable")))} />
            </div>
          </div>

          <div className="border-t border-amber-500/30 pt-3 mt-2" data-testid="coin-context-panel">
            <AnalysisMetadata
              title={t("fundamental_news")}
              lastTime={formatScheduleTime(contextSchedule?.last_completed_at, schedule?.timezone)}
              nextTime={formatScheduleTime(contextSchedule?.next_update_at, schedule?.timezone)}
              status={contextStatus}
            />
            {coinContext ? (
              <div className="space-y-2">
                <ContextSection title={t("fundamental_context")} value={localizedMarketField(coinContext.fundamental_context, lang, t("unavailable"))} />
                <ContextSection title={t("macro_news_context")} value={localizedMarketField(coinContext.macro_news_context, lang, t("unavailable"))} />
                <ContextList title={t("catalysts")} values={coinContext.catalysts} lang={lang} unavailable={t("unavailable")} tone="text-emerald-400" />
                <ContextList title={t("risks")} values={coinContext.risks} lang={lang} unavailable={t("unavailable")} tone="text-red-400" />
                <ContextSection title={t("market_outlook")} value={localizedMarketField(coinContext.context_outlook, lang, t("unavailable"))} />
                <ContextSection title={t("market_bottom_line")} value={localizedMarketField(coinContext.context_bottom_line, lang, t("unavailable"))} />
              </div>
            ) : (
              <div className="text-[10px] sm:text-xs text-zinc-400 leading-[1.45]">{t("coin_context_unavailable")}</div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function CompactSection({ title, body }) {
  return (
    <div>
      <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{title}</div>
      <div className="text-[10px] sm:text-xs text-zinc-200 leading-[1.45] break-words">{body}</div>
    </div>
  );
}

function Section({ title, body }) {
  return (
    <div>
      <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{title}</div>
      <div className="text-[10px] sm:text-xs text-zinc-200 leading-[1.45] break-words">{body}</div>
    </div>
  );
}

function AnalysisMetadata({ title, lastTime, nextTime, status }) {
  const { t } = useI18n();
  return (
    <div className="mb-2">
      <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-amber-400 whitespace-nowrap">{title}</div>
      <div className="flex flex-col items-start gap-0.5 mt-1 text-[10px] font-mono text-zinc-500" data-testid="analysis-metadata">
        <span>{t("last_update")} {lastTime}</span>
        <span>{t("next_update")} {nextTime}</span>
        <span className="text-white">{status}</span>
      </div>
    </div>
  );
}

function ContextSection({ title, value }) {
  return <div><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">{title}</div><div className="text-[10px] sm:text-xs text-zinc-200 leading-[1.4] break-words">{normalizeMarketProse(value)}</div></div>;
}

function ContextList({ title, values, tone, lang, unavailable }) {
  return <div><div className={`text-[10px] font-mono uppercase tracking-widest ${tone} mb-0.5`}>{title}</div><ul className="space-y-0.5">{(values || []).map((value, index) => <li key={`${title}-${index}`} className="text-[10px] sm:text-xs text-zinc-200 leading-[1.4] break-words">· {normalizeMarketProse(localizedMarketField(value, lang, unavailable))}</li>)}</ul></div>;
}
